import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MagicRef,
  MagicSession,
  hasSessionWork,
  modelName,
  modelResolutions,
  refName,
  refThumbUrl,
} from '../model';
import { getSession, getVisible } from '../selectors';
import { useBasket } from '../useBasket';
import { TM } from '../tokens';
import { CloseIcon, MagicIcon } from '../icons';
import { AssetThumb } from './AssetThumb';
import '../styles.scss';

// Floating bottom-right entry to Magic Studio. Empty: a single gradient pill
// ("Magic Studio") that opens an empty studio to paste/upload from scratch.
// Filled: morphs into the basket panel — a primary "Open Magic Studio (N)" CTA,
// a Clear link, and an expandable horizontal strip of staged references. The
// basket lives in the store, so it survives navigation across the catalog.
//
// It doubles as the parked-session widget: closing the studio stashes what the
// user had going, and the tray then shows that summary (prompt, settings, the
// result so far) under a "Resume" CTA — so an accidental close reads as a pause.
export const MagicBasketTray: React.FC = () => {
  const { basket, remove, clear, open } = useBasket();
  const studioOpen = useSelector(getVisible);
  const session = useSelector(getSession);
  const parked = hasSessionWork(session);

  const [expanded, setExpanded] = useState(false);
  const prevLen = useRef(basket.length);

  // Auto-expand briefly when something new is added; collapse when emptied.
  useEffect(() => {
    if (basket.length > prevLen.current && basket.length > 0) setExpanded(true);
    if (basket.length === 0) setExpanded(false);
    prevLen.current = basket.length;
  }, [basket.length]);

  // Hidden while the studio modal itself is open.
  if (studioOpen) return null;

  // ── EMPTY — nothing staged and nothing parked: a single pill that opens an
  // empty studio.
  if (basket.length === 0 && !parked) {
    return (
      <button
        type="button"
        className="al-magic-tray-pill"
        title="Open Magic Studio"
        onClick={() => open('tray')}
      >
        <span className="al-magic-tray-pill__icon">
          <MagicIcon size={15} />
        </span>
        Magic Studio
      </button>
    );
  }

  // ── FILLED — the basket panel (and the parked session, when there is one).
  return (
    <div className="al-magic-tray" style={{ maxWidth: expanded ? 520 : 340 }}>
      {/* Header — a single clean row: Open Magic Studio (N) · Clear · chevron. */}
      <div
        className="al-magic-tray__header"
        style={{ borderBottom: parked || expanded ? `1px solid ${TM.border}` : 'none' }}
      >
        <button
          type="button"
          className="al-magic-tray__open"
          title={parked ? 'Pick up where you left off' : 'Open Magic Studio'}
          onClick={() => open('tray')}
        >
          <MagicIcon size={13} />
          {parked ? 'Resume Magic Studio' : 'Open Magic Studio'}
          {basket.length > 0 && <span className="al-magic-tray__count">{basket.length}</span>}
        </button>
        <button
          type="button"
          className="al-magic-tray__clear"
          title={
            parked
              ? 'Discard this session — references, prompt, settings and result'
              : 'Clear staged references'
          }
          onClick={clear}
        >
          Clear
        </button>
        <button
          type="button"
          className="al-magic-tray__chevron"
          title={expanded ? 'Collapse' : 'Expand'}
          disabled={basket.length === 0}
          style={{ opacity: basket.length === 0 ? 0.35 : 1 }}
          onClick={() => setExpanded((e) => !e)}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform .15s ease',
            }}
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>

      {/* Parked session — what the last close left behind, so the user can see
          it's still there (and what it was) before clicking Resume. */}
      {parked && <SessionSummary session={session} onResume={() => open('tray')} />}

      {/* Expanded body — horizontal scrolling thumbnail strip. */}
      {expanded && basket.length > 0 && (
        <div className="al-magic-tray__strip">
          {basket.map((ref, i) => (
            <BasketItem key={ref.key} mref={ref} first={i === 0} onRemove={() => remove(ref.key)} />
          ))}
        </div>
      )}
    </div>
  );
};

// The parked session's card: the result so far (if any), the prompt, and the
// settings it would run with. The whole row resumes the session — the same
// action as the header CTA, since that is the only thing to do with it here.
const SessionSummary: React.FC<{ session: MagicSession; onResume: () => void }> = ({
  session,
  onResume,
}) => {
  const chips = [
    modelName(session.model),
    session.aspect,
    // Models with no size choice (OpenAI) never sent one — don't imply they did.
    modelResolutions(session.model).length > 0 ? session.resolution : null,
    // "No template" adds nothing to the prompt, so it says nothing here either.
    session.templateLabel && session.templateLabel !== 'No template' ? session.templateLabel : null,
  ].filter(Boolean);

  return (
    <button
      type="button"
      className="al-magic-tray__session"
      title="Pick up where you left off"
      onClick={onResume}
    >
      {session.result?.imageUrl ? (
        <img className="al-magic-tray__session-shot" src={session.result.imageUrl} alt="" />
      ) : (
        <span className="al-magic-tray__session-shot al-magic-tray__session-shot_empty">
          <MagicIcon size={14} />
        </span>
      )}
      <span className="al-magic-tray__session-text">
        <span className="al-magic-tray__session-prompt">
          {session.prompt.trim() || 'Result ready — no prompt typed'}
        </span>
        <span className="al-magic-tray__session-meta">{chips.join(' · ')}</span>
      </span>
    </button>
  );
};

const BasketItem: React.FC<{
  mref: MagicRef;
  first: boolean;
  onRemove: () => void;
}> = ({ mref, first, onRemove }) => {
  const [hover, setHover] = useState(false);
  const isCapture = mref.kind === 'upload';
  return (
    <div
      className="al-magic-tray__item"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <AssetThumb src={refThumbUrl(mref)} name={refName(mref)} size={80} />
      {isCapture && <span className="al-magic-tray__badge al-magic-tray__badge_frame">FRAME</span>}
      {first && <span className="al-magic-tray__badge">1ST</span>}
      <div className="al-magic-tray__item-name">{refName(mref)}</div>
      {hover && (
        <button
          type="button"
          className="al-magic-tray__item-remove"
          title="Remove"
          onClick={onRemove}
        >
          <CloseIcon size={10} />
        </button>
      )}
    </div>
  );
};
